import { useEffect } from 'react';
import { useAuth } from '../auth/hooks/useAuth';
import { landingHandoffAPI } from './api/landingHandoffAPI';
import {
  applyHandoffContinuation,
  getPendingHandoffToken,
  getPendingHandoffRetryToken,
} from './handoffContinuation';

// Module-level (not component state) so it survives remounts within the
// same page load and enforces single-flight across every mounted instance
// of this hook, not just one — two instances mounted concurrently (or one
// remounted rapidly) must never both have a request in flight at once.
//
// StrictMode note: React 18+ dev StrictMode double-invokes effects
// (mount → cleanup → mount), synchronously, within the same commit — the
// FIRST mount's cleanup runs before any microtask from that mount's async
// work has a chance to settle. Two things are needed to be safe under
// this:
//   1. Cleanup must release `retryInFlight` itself immediately (rather
//      than waiting on the abandoned closure's own `finally`, which won't
//      run until whatever it's awaiting eventually settles) — otherwise
//      the second, real effect would see the flag still held and bail out
//      forever. A release-once guard (`releasedInFlight`) prevents the
//      cleanup path and the closure's own `finally` from double-releasing,
//      or a late-settling abandoned run's `finally` from clobbering a
//      LATER, unrelated run's flag.
//   2. The actual network call must not fire synchronously inside the
//      effect body — it must first yield at least one microtask so that,
//      if this run is the abandoned first half of a StrictMode double-
//      invoke, its cleanup (which runs synchronously right after mount,
//      before that microtask) gets a chance to flip `cancelled = true`
//      and let the loop skip firing the request at all. Without this, the
//      first mount's request would already be in flight by the time
//      cleanup runs, and the second mount's request would overlap it.
const yieldToMicrotaskQueue = () => Promise.resolve();
let retryInFlight = false;
let retriesAttemptedThisLoad = 0;

const MAX_ATTEMPTS_PER_LOAD = 3;
const BACKOFF_DELAYS_MS = [500, 1500]; // between attempt 1→2 and 2→3

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const TERMINAL_CONTINUATION_TYPES = new Set([
  'handoff_claimed',
  'handoff_expired_or_invalid',
  'handoff_already_claimed',
]);

/**
 * Opportunistic, single-flight, bounded-retry-with-backoff for a landing-
 * chat-handoff claim that returned handoff_claim_failed_retryable during
 * login/register/SSO/magic-link. Mount once near the app root.
 *
 * Behavior: on authenticated mount with a pending handoff, makes up to
 * MAX_ATTEMPTS_PER_LOAD claim attempts, waiting BACKOFF_DELAYS_MS between
 * each, stopping immediately once a terminal continuation_type is
 * observed. It is NOT a persistent poller — once this bounded sequence
 * completes (whether it succeeds, hits a terminal outcome, or exhausts its
 * attempts), it does nothing further until the next full app load, at
 * which point the same cached state (if still present and not yet
 * terminal) gets a fresh bounded sequence.
 *
 * Safe under React 18+ StrictMode's dev-only double mount/cleanup/mount
 * cycle: a torn-down run releases the single-flight flag immediately in
 * its cleanup, so the StrictMode-remounted effect can start its own run
 * rather than being blocked by an abandoned, still-pending run.
 */
export default function useRetryPendingHandoffClaim() {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const pendingRetryToken = getPendingHandoffRetryToken();
    const pendingToken = getPendingHandoffToken();
    if (!pendingToken && !pendingRetryToken) return undefined;

    if (retryInFlight) return undefined;
    if (retriesAttemptedThisLoad >= MAX_ATTEMPTS_PER_LOAD) return undefined;

    let cancelled = false;
    let releasedInFlight = false;
    retryInFlight = true;

    const releaseInFlightOnce = () => {
      if (!releasedInFlight) {
        releasedInFlight = true;
        retryInFlight = false;
      }
    };

    const runBoundedRetrySequence = async () => {
      try {
        // eslint-disable-next-line no-await-in-loop
        await yieldToMicrotaskQueue();
        while (retriesAttemptedThisLoad < MAX_ATTEMPTS_PER_LOAD && !cancelled) {
          const currentRetryToken = getPendingHandoffRetryToken();
          const currentToken = getPendingHandoffToken();
          if (!currentToken && !currentRetryToken) return; // cleared elsewhere in the meantime
          if (cancelled) return; // torn down (StrictMode's abandoned first run) before firing

          retriesAttemptedThisLoad += 1;

          try {
            // eslint-disable-next-line no-await-in-loop
            const result = await landingHandoffAPI.retryClaim({
              handoffToken: currentRetryToken ? undefined : currentToken,
              handoffRetryToken: currentRetryToken || undefined,
            });
            if (cancelled) return;
            applyHandoffContinuation(result);
            if (TERMINAL_CONTINUATION_TYPES.has(result?.continuation_type)) {
              return; // done — no need to keep retrying
            }
            // handoff_claim_failed_retryable (or an unrecognized value):
            // fall through to the backoff-and-retry loop below.
          } catch (err) {
            // Network/5xx on the retry call itself: treat like a
            // retryable claim outcome and keep looping within this bounded
            // sequence — cached state stays untouched.
          }

          if (cancelled) return;

          const attemptIndex = retriesAttemptedThisLoad - 1; // 0-based
          const delay = BACKOFF_DELAYS_MS[attemptIndex];
          if (delay === undefined || retriesAttemptedThisLoad >= MAX_ATTEMPTS_PER_LOAD) break;
          // eslint-disable-next-line no-await-in-loop
          await wait(delay);
        }
      } finally {
        // Only releases if cleanup hasn't already done so for this run —
        // prevents a late-settling abandoned run from clobbering a LATER,
        // unrelated run's in-flight flag.
        releaseInFlightOnce();
      }
    };

    runBoundedRetrySequence();

    return () => {
      cancelled = true;
      // StrictMode-safe: release immediately rather than waiting for the
      // abandoned async closure above to reach its own `finally`, which
      // may not happen until after a StrictMode-remounted effect has
      // already checked `retryInFlight` and bailed out.
      releaseInFlightOnce();
    };
  }, [isAuthenticated]);
}

// Exposed for tests only: resets module-level single-flight/retry-count
// state between test cases.
export function _resetRetryStateForTests() {
  retryInFlight = false;
  retriesAttemptedThisLoad = 0;
}
